import React, { useState } from 'react';
import { InmateSupportAssignedToType } from '@reliance/types/enums/InmateSupportAssignedToType';
import { formatDate } from '../../../format/DateTime';

type InmateSupportApiResponse = {
  id?: number;
  assignedToType: string;
  dateCreated?: string;
  dateLastAssigned?: string;
};

type SupportAssignmentInfoProps = {
  inmateSupport?: InmateSupportApiResponse | null;
  inmateId?: number;
  onReassign?: (assignedToType: InmateSupportAssignedToType) => Promise<void>;
};

const SupportAssignmentInfo: React.FC<SupportAssignmentInfoProps> = ({ inmateSupport, inmateId, onReassign }) => {
  const assignedToType = inmateSupport?.assignedToType || InmateSupportAssignedToType.STAFF;
  const dateLastAssigned = inmateSupport?.dateLastAssigned;
  const [isHovered, setIsHovered] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  const isStaff = assignedToType === InmateSupportAssignedToType.STAFF;
  const alertClass = isStaff ? 'alert-warning' : 'alert-info';
  const iconClass = isStaff ? 'fas fa-id-badge' : 'fas fa-robot';

  // Opposite assignment type for reassignment
  const oppositeType = isStaff ? InmateSupportAssignedToType.AI : InmateSupportAssignedToType.STAFF;
  const oppositeIconClass = isStaff ? 'fas fa-robot' : 'fas fa-id-badge';
  const reassignDefaultBgColor = isStaff
    ? '#0d6efd'
    : '#fff3cd';
  const reassignHoverBgColor = isStaff
    ? '#0b5ed7' // Slightly darker blue
    : '#e6c200'; // Slightly darker yellow
  const reassignTextColor = isStaff
    ? '#ffffff'
    : '#664d03';

  const handleReassign = async () => {
    if (!onReassign || !inmateId || isReassigning) {
      return;
    }

    setIsReassigning(true);
    try {
      await onReassign(oppositeType);
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsReassigning(false);
    }
  };

  return (
    <div className={`alert ${alertClass} mb-3 d-flex p-0`}>
      <div className="d-flex align-items-center p-3 flex-grow-1">
        <div className="me-3 d-flex align-items-center">
          <i className={`${iconClass} fa-3x`}></i>
        </div>
        <div>
          <strong>Assigned to: {assignedToType}</strong>
          {dateLastAssigned && (
            <div className="mt-1">
              <small>Last assigned: {formatDate(dateLastAssigned)}</small>
            </div>
          )}
        </div>
      </div>
      <div
        className="d-flex align-items-center p-3"
        onClick={handleReassign}
        onMouseEnter={() => !isReassigning && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          cursor: isReassigning ? 'wait' : 'pointer',
          backgroundColor: isHovered ? reassignHoverBgColor : reassignDefaultBgColor,
          color: reassignTextColor,
          transition: 'background-color 0.2s ease',
          opacity: isReassigning ? 0.7 : 1
        }}>
        {isReassigning ? (
          <i className="fas fa-spinner fa-spin fa-2x me-2"></i>
        ) : (
          <i className={`${oppositeIconClass} fa-2x me-2`}></i>
        )}
        <div>
          <strong>{isReassigning ? 'Reassigning...' : `Reassign to ${oppositeType}`}</strong>
        </div>
      </div>
    </div>
  );
};

export default SupportAssignmentInfo;
